import { type AnyAction } from 'redux';
import { put, call, takeLatest, type CallEffect, type PutEffect, type ForkEffect } from 'redux-saga/effects';
import { UserActionType } from './enums';
import {
	fetchUserDetails,
	updateUserDetails,
	updateProfilePicture,
	fetchUserPermissions,
	updateUserPermissions,
	fetchCredits,
	updateCredits
} from './api';
import dayjs from 'dayjs';
import { uploadFile } from '@utilities';
import { epochToDate } from '@utilities/date';

type UserSagaEffect = Generator<CallEffect<any> | PutEffect<any>>;
type UserSagaForEffect = Generator<ForkEffect<void>>;

export function* fetchUserDetailsEffect(action: AnyAction): UserSagaEffect {
	const { successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchUserDetails);
		yield put({
			type: UserActionType.STORE_USER_DETAILS,
			payload: {
				...data,
				dateOfBirth: epochToDate(data.dateOfBirth)
			}
		});
		successCB(data);
	} catch (error) {
		errorCB();
		yield put({
			type: UserActionType.ERROR_USER_DETAILS,
			payload: {
				error
			}
		});
	}
}

export function* updateUserDetailsEffect(action: AnyAction): UserSagaEffect {
	try {
		const { successCB, formData } = action.payload;
		yield call(updateUserDetails, { ...formData, dateOfBirth: +dayjs(formData.dateOfBirth.startDate) });
		successCB();
	} catch (error) {
		yield put({
			type: UserActionType.ERROR_USER_DETAILS,
			payload: {
				error
			}
		});
	}
}

export function* updateProfilePictureEffect(action: AnyAction): UserSagaEffect {
	try {
		const { successCB, file } = action.payload;
		const { data }: any = yield call(updateProfilePicture, { fileName: file[0].name });
		yield call(uploadFile, data.documentUrl, file[0], data.fileName);
		successCB();
	} catch (error) {
		yield put({
			type: UserActionType.ERROR_USER_DETAILS,
			payload: {
				error
			}
		});
	}
}

export function* fetchUserPermissionsEffect(action: AnyAction): UserSagaEffect {
	const { successCB, errorCB, userId } = action.payload;
	try {
		const { data }: any = yield call(fetchUserPermissions, userId);
		yield put({ type: UserActionType.STORE_USER_PERMISSIONS, payload: data.permissions });
		if (successCB) successCB();
	} catch (err: any) {
		errorCB(err.message);
		yield put({ type: UserActionType.ERROR_USER_PERMISSIONS });
	}
}

export function* fetchSelectedUserPermissionsEffect(action: AnyAction): UserSagaEffect {
	const { successCB, errorCB, userId } = action.payload;
	try {
		const { data }: any = yield call(fetchUserPermissions, userId);
		yield put({ type: UserActionType.STORE_SELECTED_USER_PERMISSIONS, payload: data.permissions });
		if (successCB) successCB();
	} catch (err: any) {
		errorCB(err.message);
		yield put({ type: UserActionType.ERROR_SELECTED_USER_PERMISSIONS });
	}
}

export function* updateUserPermissionsEffect(action: AnyAction): UserSagaEffect {
	const { userId, successCB, errorCB, finalCB, permissions } = action.payload;
	try {
		yield call(updateUserPermissions, { permissions }, userId);

		successCB();
	} catch (error: any) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* fetchCreditsEffect(action: AnyAction): UserSagaEffect {
	const { userId, successCB, errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchCredits, userId);
		const credits = Number(data.creditLimit ?? 0) - Number(data.creditsUsed ?? 0);
		yield put({
			type: UserActionType.STORE_CREDITS,
			payload: {
				credits,
				creditLimit: data.creditLimit ?? 0,
				creditsUsed: data.creditsUsed ?? 0
			}
		});
		if (successCB) successCB({ ...data, credits });
	} catch (error: any) {
		if (errorCB) errorCB(error.message);
		yield put({
			type: UserActionType.ERROR_FETCH_CREDITS,
			payload: {
				error
			}
		});
	}
}

export function* updateCreditsEffect(action: AnyAction): UserSagaEffect {
	const { userId, credits, successCB, errorCB } = action.payload;

	try {
		yield call(updateCredits, userId, credits);
		yield put({
			type: UserActionType.UPDATE_CREDITS_SUCCESS,
			payload: {
				credits
			}
		});
		if (successCB) successCB();
	} catch (error: any) {
		if (errorCB) errorCB(error.message);
		yield put({
			type: UserActionType.ERROR_UPDATE_CREDITS,
			payload: {
				error
			}
		});
	}
}

export function* fetchUserDetailsSaga(): UserSagaForEffect {
	yield takeLatest(UserActionType.FETCH_USER_DETAILS, fetchUserDetailsEffect);
}

export function* updateUserDetailsSaga(): UserSagaForEffect {
	yield takeLatest(UserActionType.UPDATE_USER_DETAILS, updateUserDetailsEffect);
}

export function* updateProfilePictureSaga(): UserSagaForEffect {
	yield takeLatest(UserActionType.UPDATE_PROFILE_PICTURE, updateProfilePictureEffect);
}

export function* fetchUserPermissionsSaga(): UserSagaForEffect {
	yield takeLatest(UserActionType.FETCH_USER_PERMISSIONS, fetchUserPermissionsEffect);
}

export function* fetchSelectedUserPermissionsSaga(): UserSagaForEffect {
	yield takeLatest(UserActionType.FETCH_SELECTED_USER_PERMISSIONS, fetchSelectedUserPermissionsEffect);
}

export function* updateUserPermissionsSaga(): UserSagaForEffect {
	yield takeLatest(UserActionType.UPDATE_USER_PERMISSIONS, updateUserPermissionsEffect);
}

export function* fetchCreditsSaga(): UserSagaForEffect {
	yield takeLatest(UserActionType.FETCH_CREDITS, fetchCreditsEffect);
}

export function* updateCreditsSaga(): UserSagaForEffect {
	yield takeLatest(UserActionType.UPDATE_CREDITS, updateCreditsEffect);
}
